import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { History, CheckCircle, Clock, AlertCircle, ChevronLeft, ChevronDown, ChevronRight, RefreshCw, Loader2, TrendingUp, DollarSign, Calendar, Trash2, Pencil, Users, Target } from 'lucide-react';
import { PaymentPeriod, PaymentDistribution, PaymentProjectSnapshot } from '@/types/project';
import { projectAPI } from '@/utils/supabase/client';
import { EditSnapshotDialog } from './EditSnapshotDialog';

const STRATEGY_PARTNERS = ['Ken', 'Sean', 'Tim', 'Trav', 'Chase'];

interface PaymentHistoryProps {
  onBack?: () => void;
  isAdmin?: boolean;
}

export function PaymentHistory({ onBack, isAdmin = false }: PaymentHistoryProps) {
  const [payments, setPayments] = useState<PaymentPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);
  const [periodDetails, setPeriodDetails] = useState<Record<string, PaymentPeriod>>({});
  const [loadingDetails, setLoadingDetails] = useState<string | null>(null);
  const [allDetailsLoaded, setAllDetailsLoaded] = useState(false);
  const [deletingPeriod, setDeletingPeriod] = useState<string | null>(null);
  const [periodToDelete, setPeriodToDelete] = useState<PaymentPeriod | null>(null);
  const [snapshotToEdit, setSnapshotToEdit] = useState<PaymentProjectSnapshot | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const toggleProjectExpanded = (snapshotId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(snapshotId)) {
        next.delete(snapshotId);
      } else {
        next.add(snapshotId);
      }
      return next;
    });
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatMonth = (monthYear: string, isOffCycle?: boolean) => {
    const [year, monthNum] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return isOffCycle ? `${monthName} (Off-Cycle)` : monthName;
  };

  // Create a unique key for each payment period (month + cycle type)
  const getPaymentKey = (payment: PaymentPeriod) => {
    return `${payment.month_year}-${payment.is_off_cycle ? 'off' : 'main'}`;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectAPI.getPaymentPeriods();
      setPayments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodDetails = async (payment: PaymentPeriod) => {
    const key = getPaymentKey(payment);
    if (periodDetails[key]) {
      setExpandedPeriod(expandedPeriod === key ? null : key);
      return;
    }

    setLoadingDetails(key);
    try {
      const details = await projectAPI.getPaymentPeriod(payment.month_year, payment.is_off_cycle);
      setPeriodDetails(prev => ({ ...prev, [key]: details }));
      setExpandedPeriod(key);
    } catch (err) {
      console.error('Failed to load period details:', err);
    } finally {
      setLoadingDetails(null);
    }
  };

  const handleDeletePeriod = async (payment: PaymentPeriod) => {
    const key = getPaymentKey(payment);

    setDeletingPeriod(key);
    try {
      await projectAPI.deletePaymentPeriod(payment.month_year, payment.is_off_cycle);
      // Remove from local state
      setPayments(prev => prev.filter(p => getPaymentKey(p) !== key));
      setPeriodDetails(prev => {
        const newDetails = { ...prev };
        delete newDetails[key];
        return newDetails;
      });
      if (expandedPeriod === key) {
        setExpandedPeriod(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete payment period');
    } finally {
      setDeletingPeriod(null);
    }
  };

  const handleSaveSnapshot = async (snapshotId: string, updates: Partial<PaymentProjectSnapshot>) => {
    await projectAPI.updatePaymentSnapshot(snapshotId, updates);

    // Update local state - find and update the snapshot in periodDetails
    setPeriodDetails(prev => {
      const newDetails = { ...prev };
      for (const monthYear in newDetails) {
        const period = newDetails[monthYear];
        if (period.snapshots) {
          const snapshotIndex = period.snapshots.findIndex(s => s.id === snapshotId);
          if (snapshotIndex !== -1) {
            period.snapshots[snapshotIndex] = {
              ...period.snapshots[snapshotIndex],
              ...updates
            };
            break;
          }
        }
      }
      return newDetails;
    });
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  // Fetch all period details for charting when payments are loaded
  useEffect(() => {
    const fetchAllDetails = async () => {
      if (payments.length === 0 || allDetailsLoaded) return;

      const detailsToFetch = payments.filter(p => !periodDetails[getPaymentKey(p)]);
      if (detailsToFetch.length === 0) {
        setAllDetailsLoaded(true);
        return;
      }

      try {
        const results = await Promise.all(
          detailsToFetch.map(p => projectAPI.getPaymentPeriod(p.month_year, p.is_off_cycle))
        );

        const newDetails: Record<string, PaymentPeriod> = { ...periodDetails };
        results.forEach((detail, index) => {
          if (detail) {
            const key = getPaymentKey(detailsToFetch[index]);
            newDetails[key] = detail;
          }
        });

        setPeriodDetails(newDetails);
        setAllDetailsLoaded(true);
      } catch (err) {
        console.error('Failed to fetch all period details:', err);
      }
    };

    fetchAllDetails();
  }, [payments, allDetailsLoaded, periodDetails]);

  // Transform payment data for charts
  const chartData = useMemo(() => {
    // Sort payments by month (oldest first for charts), then by cycle type (main first)
    const sortedPayments = [...payments].sort((a, b) => {
      const monthCompare = a.month_year.localeCompare(b.month_year);
      if (monthCompare !== 0) return monthCompare;
      // Main cycle before off-cycle
      return (a.is_off_cycle ? 1 : 0) - (b.is_off_cycle ? 1 : 0);
    });

    return sortedPayments.map(payment => {
      const key = getPaymentKey(payment);
      const details = periodDetails[key];
      const ownerTotals: Record<string, number> = {};

      // Initialize all owners
      STRATEGY_PARTNERS.forEach(owner => {
        ownerTotals[owner] = 0;
      });

      // Sum distributions by owner
      if (details?.distributions) {
        details.distributions.forEach(dist => {
          if (STRATEGY_PARTNERS.includes(dist.recipient_name)) {
            ownerTotals[dist.recipient_name] += dist.amount;
          }
        });
      }

      return {
        month: formatMonth(payment.month_year, payment.is_off_cycle),
        monthYear: payment.month_year,
        isOffCycle: payment.is_off_cycle,
        totalRevenue: payment.total_revenue || 0,
        totalProfit: payment.total_profit || 0,
        ...ownerTotals,
      };
    });
  }, [payments, periodDetails]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalRevenue = payments.reduce((sum, p) => sum + (p.total_revenue || 0), 0);
    const totalProfit = payments.reduce((sum, p) => sum + (p.total_profit || 0), 0);
    const avgRevenue = payments.length > 0 ? totalRevenue / payments.length : 0;

    return {
      totalRevenue,
      totalProfit,
      avgRevenue,
      monthsTracked: payments.length,
    };
  }, [payments]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-success/15 text-success hover:bg-success/15">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'corrected':
        return (
          <Badge variant="outline" className="text-warning border-warning/40">
            <AlertCircle className="h-3 w-3 mr-1" />
            Corrected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Group distributions by recipient
  const groupDistributionsByRecipient = (distributions: PaymentDistribution[]) => {
    const grouped: Record<string, {
      name: string;
      profit_share: number;
      operations_compensation: number;
      tactical_compensation: number;
      kicker_bonus: number;
      total: number;
    }> = {};

    distributions.forEach(dist => {
      if (!grouped[dist.recipient_name]) {
        grouped[dist.recipient_name] = {
          name: dist.recipient_name,
          profit_share: 0,
          operations_compensation: 0,
          tactical_compensation: 0,
          kicker_bonus: 0,
          total: 0,
        };
      }
      // Map external_kicker_payout to kicker_bonus for display
      const key = dist.distribution_type === 'external_kicker_payout' ? 'kicker_bonus' : dist.distribution_type as 'profit_share' | 'operations_compensation' | 'tactical_compensation' | 'kicker_bonus';
      if (key in grouped[dist.recipient_name]) {
        grouped[dist.recipient_name][key] += dist.amount;
      }
      grouped[dist.recipient_name].total += dist.amount;
    });

    return Object.values(grouped).sort((a, b) => b.total - a.total);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={fetchPayments} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <History className="h-6 w-6" />
              Payment History
            </h2>
            <p className="text-muted-foreground">View historical payment records and distributions</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPayments}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats & Charts */}
      {payments.length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Revenue (All Time)</p>
                    <p className="text-xl font-bold font-serif">{formatCurrency(summaryStats.totalRevenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-positive" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Profit (All Time)</p>
                    <p className="text-xl font-bold font-serif text-positive">{formatCurrency(summaryStats.totalProfit)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Monthly Revenue</p>
                    <p className="text-xl font-bold font-serif">{formatCurrency(summaryStats.avgRevenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Months Tracked</p>
                    <p className="text-xl font-bold font-serif">{summaryStats.monthsTracked}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue & Profit Trends Chart */}
          {chartData.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Revenue & Profit Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="totalRevenue"
                        stroke="var(--primary)"
                        name="Total Revenue"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="totalProfit"
                        stroke="var(--accent)"
                        name="Total Profit"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Owner Compensation Trends Chart */}
          {chartData.length > 1 && allDetailsLoaded && (
            <Card>
              <CardHeader>
                <CardTitle>Owner Compensation Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="Ken" fill="var(--chart-1)" name="Ken" />
                      <Bar dataKey="Sean" fill="var(--chart-2)" name="Sean" />
                      <Bar dataKey="Tim" fill="var(--chart-3)" name="Tim" />
                      <Bar dataKey="Trav" fill="var(--chart-4)" name="Trav" />
                      <Bar dataKey="Chase" fill="var(--chart-5)" name="Chase" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Separator />
        </>
      )}

      {/* Payment List */}
      {payments.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center gap-4 text-center">
              <History className="h-12 w-12 text-muted-foreground" />
              <div>
                <p className="font-medium">No Payment History</p>
                <p className="text-sm text-muted-foreground">
                  Payment records will appear here once you mark a month as paid.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible value={expandedPeriod || undefined}>
          {payments.map(payment => {
            const paymentKey = getPaymentKey(payment);
            return (
            <AccordionItem key={payment.id} value={paymentKey}>
              <Card className="mb-2">
                <AccordionTrigger
                  className="px-4 py-3 hover:no-underline"
                  onClick={() => fetchPeriodDetails(payment)}
                >
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-4">
                      <div className="text-left">
                        <p className="font-semibold flex items-center gap-2">
                          {formatMonth(payment.month_year)}
                          {payment.is_off_cycle && (
                            <Badge variant="outline" className="text-xs">Off-Cycle</Badge>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Paid on {formatDate(payment.paid_at)}
                        </p>
                      </div>
                      {getStatusBadge(payment.status)}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="font-semibold font-serif">{formatCurrency(payment.total_revenue)}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {loadingDetails === paymentKey ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : periodDetails[paymentKey] ? (
                    <div className="px-4 pb-4 space-y-4">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-sm text-muted-foreground">Revenue</p>
                            <p className="text-lg font-semibold font-serif">
                              {formatCurrency(periodDetails[paymentKey].total_revenue)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-sm text-muted-foreground">Profit</p>
                            <p className="text-lg font-semibold font-serif text-positive">
                              {formatCurrency(periodDetails[paymentKey].total_profit)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-sm text-muted-foreground">Operating Costs</p>
                            <p className="text-lg font-semibold font-serif">
                              {formatCurrency(periodDetails[paymentKey].total_operating_costs)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-sm text-muted-foreground">Bonus Revenue</p>
                            <p className="text-lg font-semibold font-serif">
                              {formatCurrency(periodDetails[paymentKey].total_kicker_revenue)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <Separator />

                      {/* Distributions Table */}
                      <div>
                        <h4 className="font-medium mb-2">Distributions</h4>
                        {periodDetails[paymentKey].distributions &&
                        periodDetails[paymentKey].distributions!.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Recipient</TableHead>
                                <TableHead className="text-right">Profit Share</TableHead>
                                <TableHead className="text-right">Operations</TableHead>
                                <TableHead className="text-right">Team</TableHead>
                                <TableHead className="text-right">Bonus</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupDistributionsByRecipient(
                                periodDetails[paymentKey].distributions!
                              ).map(recipient => (
                                <TableRow key={recipient.name}>
                                  <TableCell className="font-medium">{recipient.name}</TableCell>
                                  <TableCell className="text-right">
                                    {recipient.profit_share > 0 ? formatCurrency(recipient.profit_share) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {recipient.operations_compensation > 0
                                      ? formatCurrency(recipient.operations_compensation)
                                      : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {recipient.tactical_compensation > 0
                                      ? formatCurrency(recipient.tactical_compensation)
                                      : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {recipient.kicker_bonus > 0 ? formatCurrency(recipient.kicker_bonus) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold font-serif text-positive">
                                    {formatCurrency(recipient.total)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-sm text-muted-foreground">No distribution records found.</p>
                        )}
                      </div>

                      {/* Projects Snapshot - Expandable */}
                      {periodDetails[paymentKey].snapshots &&
                        periodDetails[paymentKey].snapshots!.length > 0 && (
                          <>
                            <Separator />
                            <div>
                              <h4 className="font-medium mb-3">
                                Projects Included ({periodDetails[paymentKey].snapshots!.length})
                              </h4>
                              <div className="space-y-2">
                                {periodDetails[paymentKey].snapshots!.map(snapshot => {
                                  const isExpanded = expandedProjects.has(snapshot.id);
                                  const config = snapshot.full_config_json?.baseTemplate || {};
                                  const tacticalSpecialists = config.tacticalSpecialists || [];
                                  const performanceKickers = config.performanceKickers || [];

                                  return (
                                    <Card key={snapshot.id} className="overflow-hidden">
                                      {/* Project Header - Always Visible */}
                                      <div
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                                        onClick={() => toggleProjectExpanded(snapshot.id)}
                                      >
                                        <div className="flex items-center gap-3">
                                          {isExpanded ? (
                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                          )}
                                          <div>
                                            <p className="font-medium">{snapshot.project_name}</p>
                                            <p className="text-xs text-muted-foreground">
                                              Revenue: {formatCurrency(snapshot.revenue)} • Margin: {snapshot.profit_margin}%
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs">
                                            {formatCurrency(snapshot.calculated_net_profit)} profit
                                          </Badge>
                                          {isAdmin && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-7 p-0"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSnapshotToEdit(snapshot);
                                              }}
                                            >
                                              <Pencil className="h-3 w-3" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Expanded Details */}
                                      {isExpanded && (
                                        <div className="border-t bg-muted/30 p-4 space-y-4">
                                          {/* Financial Summary */}
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="text-sm">
                                              <p className="text-muted-foreground text-xs">Revenue</p>
                                              <p className="font-medium">{formatCurrency(snapshot.revenue)}</p>
                                            </div>
                                            <div className="text-sm">
                                              <p className="text-muted-foreground text-xs">Profit Margin</p>
                                              <p className="font-medium">{snapshot.profit_margin}%</p>
                                            </div>
                                            <div className="text-sm">
                                              <p className="text-muted-foreground text-xs">Operations Cost</p>
                                              <p className="font-medium">{formatCurrency(snapshot.calculated_operations_cost)}</p>
                                            </div>
                                            <div className="text-sm">
                                              <p className="text-muted-foreground text-xs">Team Cost</p>
                                              <p className="font-medium">{formatCurrency(snapshot.calculated_tactical_cost)}</p>
                                            </div>
                                          </div>

                                          {/* Team Members */}
                                          {tacticalSpecialists.length > 0 && (
                                            <div>
                                              <div className="flex items-center gap-2 mb-2">
                                                <Users className="h-4 w-4 text-muted-foreground" />
                                                <h5 className="text-sm font-medium">Team Members</h5>
                                              </div>
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {tacticalSpecialists.map((spec: any, idx: number) => (
                                                  <div key={idx} className="flex items-center justify-between text-sm bg-background/50 rounded px-3 py-2">
                                                    <span>{spec.name}</span>
                                                    <div className="flex items-center gap-2">
                                                      {spec.assignedOwner && (
                                                        <Badge variant="outline" className="text-xs">{spec.assignedOwner}</Badge>
                                                      )}
                                                      <span className="font-medium">{formatCurrency(spec.amount)}</span>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Performance Bonuses */}
                                          {performanceKickers.length > 0 && (
                                            <div>
                                              <div className="flex items-center gap-2 mb-2">
                                                <Target className="h-4 w-4 text-muted-foreground" />
                                                <h5 className="text-sm font-medium">Performance Bonuses</h5>
                                              </div>
                                              <div className="space-y-2">
                                                {performanceKickers.map((kicker: any, idx: number) => {
                                                  const tierLabels: Record<string, string> = {
                                                    'none': 'None',
                                                    'tier1': 'Tier 1 (5%)',
                                                    'tier2': 'Tier 2 (7.5%)',
                                                    'tier3': 'Tier 3 (10%)'
                                                  };
                                                  const tierPercentages: Record<string, number> = {
                                                    'none': 0,
                                                    'tier1': 0.05,
                                                    'tier2': 0.075,
                                                    'tier3': 0.10
                                                  };
                                                  const kickerRevenue = kicker.marketingSpend * (tierPercentages[kicker.selectedTier] || 0);

                                                  return (
                                                    <div key={idx} className="text-sm bg-background/50 rounded px-3 py-2">
                                                      <div className="flex items-center justify-between">
                                                        <span className="font-medium">{kicker.name}</span>
                                                        <Badge variant={kicker.selectedTier !== 'none' ? 'default' : 'secondary'} className="text-xs">
                                                          {tierLabels[kicker.selectedTier] || kicker.selectedTier}
                                                        </Badge>
                                                      </div>
                                                      <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                                                        <span>Marketing Spend: {formatCurrency(kicker.marketingSpend)}</span>
                                                        {kickerRevenue > 0 && (
                                                          <span className="text-positive font-medium">
                                                            +{formatCurrency(kickerRevenue)} bonus
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}

                                          {/* Operations Info */}
                                          {(config.operationsOwners?.length > 0 || config.strategySponsor) && (
                                            <div className="text-sm text-muted-foreground border-t pt-3 mt-3">
                                              {config.strategySponsor && (
                                                <p>Strategy Sponsor: <span className="text-foreground">{config.strategySponsor}</span></p>
                                              )}
                                              {config.operationsOwners?.length > 0 && (
                                                <p>Operations: <span className="text-foreground">{config.operationsOwners.join(', ')}</span></p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </Card>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}

                      {/* Admin Actions */}
                      {isAdmin && (
                        <>
                          <Separator />
                          <div className="flex justify-end pt-2">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setPeriodToDelete(payment)}
                              disabled={deletingPeriod === paymentKey}
                            >
                              {deletingPeriod === paymentKey ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                              Delete Payment Record
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : null}
                </AccordionContent>
              </Card>
            </AccordionItem>
          );
          })}
        </Accordion>
      )}

      {/* Edit Snapshot Dialog */}
      <EditSnapshotDialog
        open={snapshotToEdit !== null}
        onOpenChange={(open) => {
          if (!open) setSnapshotToEdit(null);
        }}
        snapshot={snapshotToEdit}
        onSave={handleSaveSnapshot}
      />

      {/* Delete period confirmation */}
      <AlertDialog
        open={periodToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPeriodToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payment record?</AlertDialogTitle>
            <AlertDialogDescription>
              {periodToDelete
                ? `This permanently deletes the payment record for ${formatMonth(periodToDelete.month_year, periodToDelete.is_off_cycle)}. This cannot be undone.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const p = periodToDelete;
                setPeriodToDelete(null);
                if (p) handleDeletePeriod(p);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
